import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { useAddHashKey } from "@/features/auth/api/hash.queries";
import { useGetApiToken } from "@/features/tokens/api/token.queries";
import type { IApiKey } from "@/features/tokens/types/token.types";
import { extractErrorMessage } from "@/lib/errors";
import {
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  createMonitoringIcon,
  type MonitoringType,
} from "@/constants/monitoring";

import z from "zod";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";

interface AddNewHashKeyFormProps {
  onSuccess?: () => void;
}

const addNewHashKeySchema = z.object({
  name: z.string().min(1, "Name is required"),
});

export default function AddNewHashKeyForm({
  onSuccess,
}: AddNewHashKeyFormProps) {
  const form = useForm<z.infer<typeof addNewHashKeySchema>>({
    resolver: zodResolver(addNewHashKeySchema),
    defaultValues: {
      name: "",
    },
  });

  // Fetch API keys
  const { data: apiKeysData, isLoading: isLoadingApiKeys } = useGetApiToken(
    1,
    100,
    true,
  );
  const apiKeys = apiKeysData?.api_keys || [];

  // Track selected API keys
  const [selectedApiKeys, setSelectedApiKeys] = useState<string[]>([]);

  const { mutate: addHashKey, isPending } = useAddHashKey();

  const handleApiKeyToggle = (apiKeyId: string) => {
    setSelectedApiKeys((prev) => {
      if (prev.includes(apiKeyId)) {
        return prev.filter((id) => id !== apiKeyId);
      }
      return [...prev, apiKeyId];
    });
  };

  // Helper to get monitoring type from API key prefix
  const getMonitoringTypeFromApiKey = (
    apiKeyPreview: string,
  ): MonitoringType => {
    return getMonitoringTypeFromToken(apiKeyPreview);
  };

  function onSubmit(values: z.infer<typeof addNewHashKeySchema>) {
    // selectedApiKeys is already an array of selected IDs
    addHashKey(
      {
        api_key_ids: selectedApiKeys,
        hash_key_name: values.name,
        description: "",
      },
      {
        onSuccess: () => {
          form.reset();
          setSelectedApiKeys([]);
          onSuccess?.();
        },
        onError: (err) => {
          const message = extractErrorMessage(err);
          toast.error(message);
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col space-y-4"
      >
        {/* Name Field */}
        <div className="grid grid-cols-1 items-start gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-sm font-bold text-neutral-800">
                  Name <span className="text-red-600">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Hash Key Name"
                    variant="filled"
                    className="h-[45px]"
                  />
                </FormControl>
                <FormMessage className="text-xs text-red-600" />
              </FormItem>
            )}
          />
        </div>

        {/* API Keys Selection */}
        <div className="space-y-3">
          <FormLabel className="text-sm font-bold text-neutral-800">
            Distribute API Key
          </FormLabel>

          {isLoadingApiKeys ? (
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 rounded-lg border-2 border-neutral-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex w-full items-center gap-2">
                    <div className="h-[28px] w-[28px] flex-shrink-0 animate-pulse rounded-[8px] bg-neutral-300" />
                    <div className="h-4 w-full min-w-0 flex-1 animate-pulse rounded bg-neutral-300" />
                    <div className="h-6 w-11 flex-shrink-0 animate-pulse rounded-full bg-neutral-300" />
                  </div>
                </div>
              ))}
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="rounded-lg p-4 text-center text-sm text-neutral-500">
              No API keys available
            </div>
          ) : (
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {apiKeys.map((apiKey: IApiKey) => {
                const isSelected = selectedApiKeys.includes(apiKey.api_key_id);
                const monitoringType = getMonitoringTypeFromApiKey(
                  apiKey.api_key_preview,
                );
                const isPremium = apiKey.api_key_preview
                  .toLowerCase()
                  .includes("premium");

                const colorClass = isPremium
                  ? "bg-yellow-500"
                  : MONITORING_BADGE_STYLES[monitoringType] || "bg-gray-500";

                const icon = isPremium ? (
                  <i className="ri-star-line text-md text-white" />
                ) : (
                  createMonitoringIcon(monitoringType, "text-md", true)
                );

                return (
                  <div
                    key={apiKey.api_key_id}
                    className={`flex flex-col gap-3 rounded-lg border-2 p-4 shadow-sm transition-all hover:shadow-md ${
                      isSelected
                        ? "border-orange-500 bg-orange-50"
                        : "border-neutral-200 bg-white"
                    }`}
                  >
                    {/* Token header with icon and switch */}
                    <div className="flex w-full items-center gap-2">
                      <div
                        className={`flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-[8px] ${colorClass} p-2 font-medium text-white transition-transform duration-200 hover:scale-105`}
                      >
                        <div className="flex items-center justify-center text-[20px]">
                          {icon}
                        </div>
                      </div>
                      <div className="line-clamp-2 w-full min-w-0 flex-1 text-xs leading-4 font-semibold -tracking-wide break-words text-neutral-950">
                        {apiKey.api_key_preview}
                      </div>

                      <Switch
                        checked={isSelected}
                        onCheckedChange={() =>
                          handleApiKeyToggle(apiKey.api_key_id)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="mt-3 h-[48px] w-full"
          disabled={isPending || selectedApiKeys.length === 0}
        >
          {isPending ? (
            <div className="flex h-full items-center justify-center text-white">
              <Spinner size="sm" className="stroke-white!" />
            </div>
          ) : (
            `Add ${selectedApiKeys.length > 0 ? ` (${selectedApiKeys.length} API key${selectedApiKeys.length !== 1 ? "s" : ""} selected)` : ""}`
          )}
        </Button>
      </form>
    </Form>
  );
}
